import { useEffect, useMemo, useState } from "react";
import { DoorOpen, IdCard, MapPin, ShieldAlert, Siren, X } from "lucide-react";
import { listSecurityEvents } from "../api";
import type { SecurityEvent, Vehicle } from "../types";

const labels = {
  panic: "BOTÓN DE PÁNICO",
  door_open: "PUERTA ABIERTA",
  driver_identified: "OPERADOR IDENTIFICADO",
};

export function SecurityPanel({
  open,
  onClose,
  vehicles,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
}) {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listSecurityEvents()
        .then(setEvents)
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar seguridad"));
    };
    refresh();
    window.addEventListener("gsf:security-updated", refresh);
    return () => window.removeEventListener("gsf:security-updated", refresh);
  }, [open]);

  const critical = useMemo(
    () => events.filter((event) => event.severity === "critical").length,
    [events],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel security-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="security-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">SEGURIDAD FÍSICA · CABINA</span>
            <h2 id="security-title">Pánico, puertas e identificación</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar seguridad"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Siren size={17} /><b>{critical}</b> críticos</span>
          <span><DoorOpen size={17} /><b>{events.filter((event) => event.event_type === "door_open").length}</b> aperturas</span>
          <span><IdCard size={17} /><b>{events.filter((event) => event.event_type === "driver_identified").length}</b> identificaciones</span>
        </section>
        <div className="security-panel__body">
          <section className="security-feed">
            <span className="eyebrow">BITÁCORA EN VIVO</span>
            {events.map((event) => {
              const vehicle = vehicles.find((item) => item.id === event.vehicle_id);
              const Icon = event.event_type === "panic" ? ShieldAlert : event.event_type === "door_open" ? DoorOpen : IdCard;
              return (
                <article className={`security-event security-event--${event.severity}`} key={event.id}>
                  <span><Icon size={18} /></span>
                  <div>
                    <small>{vehicle?.unit_code || event.vehicle_id} · {new Date(event.captured_at).toLocaleString("es-MX")}</small>
                    <strong>{labels[event.event_type as keyof typeof labels] || event.event_type}</strong>
                    <em>
                      {event.identity_code || `${event.speed_kph} km/h`}
                      <i><MapPin size={10} /> {event.latitude.toFixed(3)}, {event.longitude.toFixed(3)}</i>
                    </em>
                  </div>
                  <b>{event.status}</b>
                </article>
              );
            })}
          </section>
          <aside className="security-protocol">
            <Siren size={28} />
            <strong>PROTOCOLO DE PÁNICO</strong>
            <ol>
              <li>Crear incidente crítico.</li>
              <li>Capturar frontal y cabina.</li>
              <li>Fijar posición y velocidad.</li>
              <li>Notificar al centro C5.</li>
              <li>Conservar evidencia bloqueada.</li>
            </ol>
            <p>La inmovilización remota no se ejecuta automáticamente y requiere doble autorización.</p>
          </aside>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
